import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Gamepad2, ArrowRight, Grid3x3 } from "lucide-react";
import BeeIcon from "@/components/BeeIcon";

const GAMES = [
  {
    to: "/app/games/word",
    name: "Hive Words",
    tagline: "Spell words across a honeycomb before the timer runs out.",
    testid: "game-card-word",
    accent: "from-amber-400 to-yellow-500",
    art: <span className="text-4xl">🐝</span>,
  },
  {
    to: "/app/games/beesweeper",
    name: "Bee Hero",
    tagline: "Save the bees from being destroyed — find them all before all is lost.",
    testid: "game-card-beesweeper",
    accent: "from-amber-300 to-orange-400",
    art: <Grid3x3 className="w-9 h-9 text-amber-900/70" />,
  },
  {
    to: "/app/games/honeysort",
    name: "Honey Sort",
    tagline: "Pour and sort honey layers until every jar is a single colour — 1000+ levels.",
    testid: "game-card-honeysort",
    accent: "from-yellow-400 to-amber-500",
    art: <span className="text-4xl">🍯</span>,
  },
  {
    to: "/app/games/beematch",
    name: "Bee Match",
    tagline: "Swap and match bees, honey and blossoms — forge power pieces to hit the target.",
    testid: "game-card-beematch",
    accent: "from-pink-400 to-amber-400",
    art: <span className="text-4xl">🐝</span>,
  },
  {
    to: "/app/games/honeytycoon",
    name: "Honey Tycoon",
    tagline: "Trade honey across world markets, run your hives, and grow the bank — buy low, sell high.",
    testid: "game-card-honeytycoon",
    accent: "from-amber-500 to-orange-500",
    art: <span className="text-4xl">💰</span>,
  },
  {
    to: "/app/games/escapebees",
    name: "Escape Bees",
    tagline: "Tap the arrows out of the maze to free the trapped bees and guide them home.",
    testid: "game-card-escapebees",
    accent: "from-yellow-400 to-amber-500",
    art: <span className="text-4xl">🐝</span>,
  },
  {
    to: "/app/games/nectartrail",
    name: "Nectar Trail",
    tagline: "A bee-themed TriPeaks journey — clear each flower's peaks before the timer and carry honey home.",
    testid: "game-card-nectartrail",
    accent: "from-emerald-400 to-amber-400",
    art: <span className="text-4xl">🌼</span>,
  },
];

export default function GamesHub() {
  const { user } = useAuth();
  if (user && !user.is_admin) return <Navigate to="/app" replace />;

  return (
    <div className="space-y-6" data-testid="games-hub-page">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight flex items-center gap-2">
          <Gamepad2 className="w-7 h-7 text-amber-500" /> Games
        </h1>
        <p className="text-stone-500 mt-1">Take a break from the hive with a quick puzzle. More games coming soon!</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {GAMES.map((g) => (
          <Link
            key={g.to}
            to={g.to}
            data-testid={g.testid}
            className="group relative overflow-hidden rounded-xl border border-stone-200 bg-white hover:border-amber-300 hover:shadow-lg transition-all"
          >
            <div className={`h-20 bg-gradient-to-br ${g.accent} grid place-items-center relative`}>
              <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(0,0,0,0.25) 1px, transparent 0)", backgroundSize: "16px 16px" }} />
              <div className="relative">{g.name === "Bee Hero" ? <BeeIcon size={40} /> : g.art}</div>
            </div>
            <div className="p-3">
              <div className="flex items-center justify-between gap-1">
                <h2 className="font-display text-sm font-semibold text-stone-800 leading-tight">{g.name}</h2>
                <ArrowRight className="w-4 h-4 shrink-0 text-stone-400 group-hover:text-amber-500 group-hover:translate-x-0.5 transition-all" />
              </div>
              <p className="text-[11px] leading-snug text-stone-500 mt-1 line-clamp-2">{g.tagline}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
